import React, {useEffect, useState} from 'react';
import {Button, Col, Collapse, message, Modal, Row, Table} from 'antd';
import {
    CommandDto,
    ExportControllerApi, LocateDto,
    PlaywrightControllerApi,
    TestResultControllerApi,
    TestResultDto,
    TestStep, Trace
} from "../../../api";
import "../../../styles.css";
import {useKeycloakHeader} from "../../../config/Config";
import {
    formatDateTime,
    getErrorMessage,
    getTimeOffsetString,
    translateCommandType,
    translateConditionToReadableString
} from "../../../utils/Utils";
import {DownloadOutlined, EyeOutlined} from '@ant-design/icons';
import {downloadFile} from "../../../utils/FileDownload";
import {COLORS} from "../../../utils/Colors";
import {locateDtoToList} from "../../../utils/LocatorUtils";
import {AxiosRequestConfig} from "axios";


/*
Dieses Modal zeigt die Testergebnisse einer Testausführung an
 */
const TestResultModal: React.FC<{
    visible: boolean,
    testResultId?: number,
    testResult?: TestResultDto,
    traceUrl?: string,
    onClose: () => void
}> = ({
          visible,
          testResultId,
          testResult,
          traceUrl,
          onClose
      }) => {
    const [testResultDto, setTestResultDto] = useState<TestResultDto>();
    const [traces, setTraces] = useState<Trace[]>([]);

    const headers = useKeycloakHeader();
    const testResultControllerApi = new TestResultControllerApi();
    const exportControllerApi = new ExportControllerApi();
    const playwrightControllerApi = new PlaywrightControllerApi();

    useEffect(() => {
        if (testResultId) {
            fetchTestResultData(testResultId);
            fetchTraces(testResultId);
        }
        if (testResult) {
            setTestResultDto(testResult);
        }
    }, [testResultId, testResult]);

    useEffect(() => {

    }, [testResult]);

    const fetchTestResultData = (id: number) => {
        testResultControllerApi.getById(id, headers)
            .then((axiosResponse) => {
                setTestResultDto(axiosResponse.data);
            }).catch((error) => {
            let msg = "Fehler beim Abrufen der Daten: ";
            message.error(msg + getErrorMessage(error));
        });
    }

    const fetchTraces = (id: number) => {
        playwrightControllerApi.getTracesForTestResultId(id, headers)
            .then((axiosResponse) => {
                setTraces(axiosResponse.data);
            }).catch((error) => {
            let msg = "Fehler beim Abrufen der Daten: ";
            message.error(msg + getErrorMessage(error));
        });
    }

    const formatCommandData = (command: CommandDto | undefined) => {
        if (!command) {
            return [];
        }

        let locateDtos: LocateDto[] = [];
        if (command.locateDto) {
            locateDtos = locateDtoToList(command.locateDto);
        }


        const baseData = [
            {key: 'ID', value: command.id, className: "row-odd"},
            {key: 'Befehlstyp', value: command.commandType},
            {key: 'Ausführungsoption', value: command.executeOption},
            {key: 'Ausführungswert', value: command.executeValue},
            {key: 'Ausführungsziel', value: command.executeTarget?.toString()},
            {key: 'Navigations-URL', value: command.navigateUrl},
            {key: 'Prüfoption', value: command.checkStatusOption},
            {key: 'Prüfwert', value: command.checkStatusValue},
            {key: 'Wertoption', value: command.valueOption},
            {key: 'Variablenname', value: command.variableName},
            {key: 'Vergleichswert', value: command.compareValue},
            {key: 'Komponenten-ID', value: command.prefabId},
            {key: 'Komponentenname', value: command.prefabName},
            {key: 'Request ID', value: command.requestId},
            {key: 'Request Name', value: command.requestName},
            {key: 'Request URL', value: command.requestUrl},
            {key: 'Pfadvariablen', value: command.pathVariables ? JSON.stringify(command.pathVariables) : null},
            {key: 'Antwortvariablen', value: command.responseVariables ? JSON.stringify(command.responseVariables) : null},
            {key: 'Request JSON', value: command.requestJson},
            {key: 'Tab-Name', value: command.systemTab},
            {key: 'Wartezeit (ms)', value: command.waitInMs},
            {key: 'Dialog akzeptieren', value: command.dialogAccept === false ? null : "Ja"},
            {key: 'JSON-Pfad', value: command.jsonPath},
            {key: 'Attribut', value: command.attribute},
            {key: 'Bedingung', value: command.condition ? translateConditionToReadableString(command.condition) : null},
            {key: 'Beschreibung', value: command.description},
            {key: 'Zeit-Offset', value: command.timeOffset ? getTimeOffsetString(command.timeOffset) : null},
            {key: 'Import Test-ID', value: command.importTestDataCommandDto ? command.importTestDataCommandDto.testId : null},
            {
                key: 'Zeit-Offset',
                value: command.importTestDataCommandDto ? command.importTestDataCommandDto.timeOffset ? getTimeOffsetString(command.importTestDataCommandDto.timeOffset) : null : null
            },
            {
                key: 'Start-Zeit',
                value: command.importTestDataCommandDto ? formatDateTime(command.importTestDataCommandDto.startTs) != '' ? formatDateTime(command.importTestDataCommandDto.startTs) : null : null
            },
            {
                key: 'Enthält Variablen',
                value: command.importTestDataCommandDto ? JSON.stringify(command.importTestDataCommandDto.containsVariables) : null
            },
            {
                key: 'Verwendeter Test',
                value: command.importTestDataCommandDto ? command.importTestDataCommandDto.useUsedTestExecution ? 'Ja' : null : null
            },
            {
                key: 'Fehlgeschlagener Test',
                value: command.importTestDataCommandDto ? command.importTestDataCommandDto.useFailedTestExecution ? 'Ja' : null : null
            },
        ];

        const locateData = locateDtos.map((locate, index) => {
            // Alternierende Farbklassen für gerade und ungerade Indizes
            const alternateClass = index % 2 === 0 ? 'row-even' : 'row-odd';

            return [
                {key: `Locator ${index + 1} - Name`, value: locate.name, className: alternateClass},
                {key: `Locator ${index + 1} - ID`, value: locate.id, className: alternateClass},
                {key: `Locator ${index + 1} - Option`, value: locate.locateOption, className: alternateClass},
                {key: `Locator ${index + 1} - Rolle`, value: locate.role, className: alternateClass},
                {key: `Locator ${index + 1} - Prüfwert`, value: locate.checkValue, className: alternateClass},
                {key: `Locator ${index + 1} - Musterwert`, value: locate.patternValue, className: alternateClass},
                {key: `Locator ${index + 1} - Eindeutig?`, value: locate.exact ? 'Ja' : 'Nein', className: alternateClass},
                {key: `Locator ${index + 1} - Hat Text`, value: locate.filterOption?.hasText, className: alternateClass},
                {key: `Locator ${index + 1} - Hat kein Text`, value: locate.filterOption?.hasNotText, className: alternateClass},
                {key: `Locator ${index + 1} - Index`, value: locate.nthElement, className: alternateClass},
            ].filter(item => item.value != null);
        }).flat();

        const data = [...baseData, ...locateData].filter(item => item.value != null); // Remove null or undefined values

        return data;
    };


    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Wert',
            dataIndex: 'value',
            key: 'value',
        },
    ];

    const checkIfPaused = (testSteps: TestStep[], successfullSteps?: number): boolean | undefined => {
        if (testSteps && successfullSteps !== undefined) {
            if (testSteps.length > successfullSteps) {
                if (testSteps[successfullSteps - 1]?.command?.commandType === "PAUSE") {
                    return true;
                }
            }
        }
        return false;
    };

    const getClassFromStepState = (testStep: TestStep, isPaused: boolean | undefined): string => {
        if (testStep.executeState === "FAILED" && isPaused) return 'panel-paused';
        if (testStep.executeState === "FAILED") return 'panel-error';
        if (testStep.executeState === "SUCCESS") return 'panel-success';
        if (testStep.executeState === "PAUSED" && !isPaused) return 'panel-success';
        if (testStep.executeState === "PAUSED") return 'panel-paused';
        if (testStep.executeState === "IGNORED") return 'panel-ignored';
        return 'panel-error';
    }

    // Generiere die Collapse-Items für Testschritte
    const generateCollapseItems = (testSteps: TestStep[], successfullSteps?: number) => {
        let isPaused = checkIfPaused(testSteps, successfullSteps);

        return testSteps.map((step, index) => ({
            key: index,
            label: (
                <div style={{display: 'flex', alignItems: 'center'}}>
                <span>
                    {index + 1}. {step.command?.commandType ? translateCommandType(step.command.commandType) : step.commandName}
                    {step.description ? ` (${step.description})` : ''}
                    {(step.command?.commandType === "PREFAB" && step.command.condition) ? " Bedingung: {" + translateConditionToReadableString(step.command.condition) + "}" : ""}
                </span>
                    {step.prefab && (
                        <span style={{marginLeft: 8, color: COLORS.btcPrimaryMediumBlue, fontWeight: 'bold'}}>
                        [Komponente]
                    </span>
                    )}
                </div>
            ),
            className: getClassFromStepState(step, isPaused),
            children: (
                <Table
                    columns={[
                        {title: 'Schlüssel', dataIndex: 'key', key: 'key'},
                        {title: 'Wert', dataIndex: 'value', key: 'value'}
                    ]}
                    dataSource={formatCommandData(step.command)}
                    pagination={false}
                    size="small"
                    rowClassName={(record) => record.className || ''}
                />
            )
        }));
    };

    const doExportCsv = () => {
        if (testResultDto && testResultDto.id) {
            exportControllerApi.exportTestResultsToCsv([testResultDto.id], headers)
                .then(response => {
                    downloadFile(response.data, "test_results_", "csv");
                    message.success("Datei erfolgreich heruntergeladen!");
                })
                .catch(error => {
                    message.error("Fehler beim Herunterladen einer Datei!");
                });
        } else {
            message.error("Es wurde kein Testergebnis ausgewählt!");
        }
    };

    const doExportJson = () => {
        if (testResultDto && testResultDto.id) {
            exportControllerApi.exportTestResultsJson([testResultDto.id], headers)
                .then(response => {
                    downloadFile(response.data, "test_results_", "json");
                    message.success("Datei erfolgreich heruntergeladen!");
                })
                .catch(error => {
                    message.error("Fehler beim Herunterladen einer Datei!");
                });
        } else {
            message.error("Es wurde kein Testergebnis ausgewählt!");
        }
    };


    const downloadTrace = (id: number | undefined) => {
        if (id) {
            // Erweiterte Optionen für binäre Downloads
            const options: AxiosRequestConfig = {
                responseType: 'blob',
                ...headers
            };

            playwrightControllerApi.downloadTrace(id, options).then((response) => {
                message.success("Trace wird heruntergeladen...");
                // response.data sollte bereits ein Blob sein
                downloadFile(response.data, "trace", "zip");
            }).catch((error) => {
                message.error("Fehler beim Download des Traces");
                console.error('Download error:', error);
            });
        }
    }



    return (
        <Modal
            title="Details der Testausführung"
            open={visible}
            onCancel={onClose}
            footer={null}
            width={800}
            className="custom-modal" // Anwenden der benutzerdefinierten Breitenklasse
        >
            {testResultDto?.interpretedErrorMessage && <p><strong>Fehler:</strong> {testResultDto.interpretedErrorMessage}</p>}

            <h3>Testschritte:</h3>
            <Collapse items={generateCollapseItems(testResultDto?.testSteps || [], testResultDto?.successfullSteps)}/>

            <h3>Laufzeit-Variablen:</h3>
            <Table
                columns={columns}
                dataSource={testResultDto?.runtimeVariables}
                pagination={false}
                locale={{emptyText: 'Keine Variablen vorhanden'}}
            />

            <h3>Vordefinierte Variablen:</h3>
            <Table
                columns={columns}
                dataSource={testResultDto?.predefinedVariables}
                pagination={false}
                locale={{emptyText: 'Keine Variablen vorhanden'}}
            />
            <div style={{padding: '20px'}}>
                {/* Export-Buttons: nebeneinander zentriert */}
                <Row gutter={24} justify="center" style={{marginBottom: '20px'}}>
                    <Col>
                        <Button type="primary" onClick={doExportCsv} icon={<DownloadOutlined/>}>
                            CSV-Export
                        </Button>
                    </Col>
                    <Col>
                        <Button type="primary" onClick={doExportJson} icon={<DownloadOutlined/>}>
                            Json-Export
                        </Button>
                    </Col>
                </Row>

                {/* Dynamische Trace-Buttons: kompakt und übersichtlich */}
                {traces.map((trace, index) => (
                    <Row gutter={24} justify="center" style={{marginTop: '10px'}} key={trace.id}>
                        <Col span={12} style={{textAlign: 'center'}}>
                            <Button
                                style={{color: COLORS.btcTertiaryPink}}
                                onClick={() => downloadTrace(trace.id)}
                                icon={<EyeOutlined/>}
                                block
                            >
                                Aufzeichnung {index + 1}
                            </Button>
                        </Col>
                    </Row>
                ))}
                {traceUrl &&
                    <Row gutter={24} justify="center" style={{marginTop: '10px'}}>
                      <Col span={24} style={{textAlign: 'center'}}>
                        <div style={{
                            color: COLORS.btcTertiaryPink,
                            border: '1px solid ' + COLORS.btcTertiaryPink,
                            padding: '10px',
                            borderRadius: '4px'
                        }}>
                          Der Test wurde lokal ausgeführt. Bitte benutze dein lokales GLOW-Tool zum Ansehen der Aufzeichnung.
                          Die Aufzeichnung ist unter dem angegebenen Pfad {traceUrl} zu finden.
                        </div>
                      </Col>
                    </Row>
                }
            </div>
        </Modal>
    );
};

export default TestResultModal;
